import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { FormProvider, useController, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loading, PaymentMethods, PhoneNumberInputs } from "..";
import { pageColumn } from "../layout/container/pageColumn";
import JourneyFrame from "../JourneyFrame";
import SearchCustomerForm from "../SearchCustomerForm";
import ReviewSummary, { ReviewRow } from "../ReviewSummary";
import ConfiguredFields from "../fields/ConfiguredFields";
import TextInput, { fieldSx } from "../inputs/TextInput";
import FormSection from "../SchoolFee/FormSection";
import { useMaskedInput, usePayment } from "../../hooks";
import useDepositAmount from "../../hooks/useDepositAmount";
import { useStepHistory } from "../../hooks/useStepHistory";
import useMethodNumberCheck from "../../hooks/useMethodNumberCheck";
import {
  DEFAULT_PHONE_COUNTRY,
  isCompleteNationalNumber,
} from "../../constants/phoneCountries";
import {
  cleanAmountInput,
  displayAmount,
  displayPhone,
} from "../../utils/displayMask";
import {
  priceSearch,
  useQueryPaymentMethods,
  usePriceMutation,
} from "../../api/queries";
import { usePayMutation } from "../../api/queries/usePayMutation";
import { useGlobalContext } from "../../context/globalContext";
import { emailRegex } from "../../constants";
import { CorporateService } from "../../models/Service";
import { PaymentMethod } from "../../models/payment";
import { ThemeProps } from "../../models/theme";
import {
  formatCurrency,
  getFailureDisplay,
  getStorageToken,
  isErrorResponse,
  payableAmount,
  unclaimedFields,
} from "../../utils";
import type { FieldClaims } from "../../utils";
import { usePortal } from "../../context/portalContext";
import { frameProvider, isServiceLink } from "../../utils/portalTarget";
import { currencyAdornment } from "../inputs/adornments";

// D-01: a template owns its own step sequence, and the two deposit journeys do
// not share one -- the searched journey identifies a customer first. Named
// rather than a bare integer, per payment-journey-step-integrity.md; the
// in-repo precedent is context/schoolFeeContext.tsx:19-27.
export const DEPOSIT_FUND_MANUAL_STEPS = ["details", "confirm"] as const;
export const DEPOSIT_FUND_SEARCH_STEPS = [
  "identify",
  "details",
  "confirm",
] as const;

export type DepositFundStep = (typeof DEPOSIT_FUND_SEARCH_STEPS)[number];

// What the frame shows for each step: its short progress name, its title and
// the sentence under it.
const STEP_NAMES: Record<DepositFundStep, string> = {
  identify: "journey.steps.customer_code",
  details: "journey.steps.payment_details",
  confirm: "journey.steps.confirm",
};
const STEP_TITLES: Record<DepositFundStep, string> = {
  identify: "payment.steps.customer_search",
  details: "payment.steps.choose_method",
  confirm: "payment.steps.review_confirm",
};
const STEP_DESCRIPTIONS: Record<DepositFundStep, string> = {
  identify: "payment.step0_description",
  details: "payment.step1_description",
  confirm: "payment.step2_description",
};

// The codes each journey draws with its own inputs, so the configured slot
// does not draw them a second time. Claimed by PURPOSE: the phone here becomes
// instrument_account_number, the account debited, and for deposit the payer
// and the account holder are the same person.
export const DEPOSIT_FUND_MANUAL_CLAIMS: FieldClaims = [
  "CUSNAME",
  "CUSEMAIL",
  "CUSPHONE",
];

// The searched journey takes name and email from the found customer and never
// draws them, so it claims neither.
export const DEPOSIT_FUND_SEARCH_CLAIMS: FieldClaims = ["CUSPHONE"];

export type FlowTemplateProps = {
  service: CorporateService;
  theme: ThemeProps;
  corporateId: string;
};

// The half of the pay request that differs between the two journeys: a searched
// payment pays for the customer that was found, a manual one for whoever filled
// the form in. Supplied by each template so neither has to ask which it is.
type InstrumentPayload = Record<string, unknown>;

type PanelRenderer = (
  step: DepositFundStep,
  methods: PaymentMethod[],
  configuredFields: React.ReactNode,
  // The number-against-method refusal, shown under the number it is about.
  numberError: string,
  // Continue was pressed on this step: every field shows what it still lacks.
  revealed: boolean
) => React.ReactNode;

type JourneyProps = FlowTemplateProps & {
  steps: readonly DepositFundStep[];
  // Whether the payer types their own name and email, which is also what makes
  // those two fields required. The searched journey takes both from the customer.
  collectsPayerIdentity: boolean;
  // The field codes this journey draws with its own inputs. The two journeys
  // differ: a single shared list would over-claim on the searched one and
  // silently drop a field the payer should have seen.
  claims: FieldClaims;
  instrumentPayload: () => InstrumentPayload;
  renderPanel: PanelRenderer;
};

// Who the searched deposit pays for, beside the form, with the amount the
// payer types repeated on it. There is no bill behind a deposit, so it carries
// no paid or unpaid status.
const CustomerCard = ({ currency }: { currency?: string }) => {
  const { t } = useTranslation();
  const { customerUser, amount } = usePayment();
  const eyebrow = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "text.secondary",
  } as const;

  return (
    <Box
      component="section"
      aria-labelledby="deposit-customer-card"
      sx={{
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        bgcolor: grey[100],
        minWidth: 0,
        alignSelf: "start",
      }}
    >
      <Typography id="deposit-customer-card" sx={eyebrow}>
        {t("payment.form.amount")}
      </Typography>
      <Typography
        sx={{ fontSize: 26, fontWeight: 800, my: 1, whiteSpace: "nowrap" }}
      >
        {amount ? formatCurrency(amount, undefined, currency) : "…"}
      </Typography>
      <Box component="dl" sx={{ m: 0, display: "grid", gap: 1.5 }}>
        <Box>
          <Typography component="dt" sx={eyebrow}>
            {t("payment.form.customer_name")}
          </Typography>
          <Typography component="dd" sx={{ m: 0, fontWeight: 600 }}>
            {customerUser?.name || "-"}
          </Typography>
        </Box>
        <Box>
          <Typography component="dt" sx={eyebrow}>
            {t("payment.form.customer_code")}
          </Typography>
          <Typography component="dd" sx={{ m: 0, fontWeight: 600 }}>
            {customerUser?.customer_business_no || "-"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Replaces components/paymentForm.tsx. D-04: this is the template's OWN amount
// entry -- a service's configured fields render beside it, never in its place.
// A field shows its message once the payer has typed in it, or once Continue
// was pressed.
// Confirm: what the payer entered, then the server's quote. A searched
// deposit names the customer on the card beside it, so no name row here.
const DepositReview = ({
  currency,
  searched,
}: {
  currency?: string;
  searched: boolean;
}) => {
  const { t } = useTranslation();
  const { amount, name, email, phoneNumber, selectedWallet, selectedMethod } =
    usePayment();
  const number = phoneNumber || selectedWallet?.wallet_identifier || "";

  const rows: ReviewRow[] = [
    {
      label: t("review.amount"),
      value: formatCurrency(amount || "0", undefined, currency),
    },
    ...(searched
      ? []
      : [
          { label: t("review.name"), value: name.trim() || "-" },
          ...(email.trim()
            ? [{ label: t("review.email"), value: email.trim() }]
            : []),
        ]),
    {
      label: t("review.number"),
      value: number ? displayPhone(number, DEFAULT_PHONE_COUNTRY) : "-",
    },
    { label: t("review.method"), value: selectedMethod?.name ?? "-" },
  ];

  return <ReviewSummary rows={rows} currency={currency} />;
};

const PayerDetailsFields = ({
  service,
  revealed,
}: {
  service: CorporateService;
  revealed: boolean;
}) => {
  const { t } = useTranslation();
  const { amount, setAmount, name, setName, email, setEmail } = usePayment();
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const amountCopy = useDepositAmount(service?.amount, service?.currency, {
    hintKey: "payment.form.amount_hint",
    requiredKey: "payment.form.errors.amount_required",
  });
  const shows = (field: string) => revealed || !!touched[field];
  const touch = (field: string) =>
    setTouched(previous => ({ ...previous, [field]: true }));

  const maskedAmount = useMaskedInput({
    value: amount,
    clean: raw => cleanAmountInput(raw),
    display: displayAmount,
    onValueChange: value => {
      setAmount(value);
      touch("amount");
    },
  });

  const amountError = shows("amount") ? amountCopy.message(amount) : "";
  const nameError =
    shows("name") && name.trim() === ""
      ? t("payment.form.errors.name_required")
      : "";
  const emailError = !shows("email")
    ? ""
    : email.trim() === ""
    ? t("payment.form.errors.email_required")
    : !emailRegex.test(email.trim())
    ? t("payment.form.errors.email_invalid")
    : "";

  return (
    <FormSection title={t("payment.form.transaction_title")}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            sx={fieldSx}
            type="text"
            inputMode="numeric"
            id="amount"
            slotProps={currencyAdornment(service?.currency)}
            label={t("payment.form.amount_label")}
            inputRef={maskedAmount.inputRef}
            value={maskedAmount.value}
            onChange={maskedAmount.onChange}
            error={!!amountError}
            helperText={amountError || amountCopy.hint}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            sx={fieldSx}
            type="text"
            id="name"
            label={`${t("payment.form.name_label")}*`}
            value={name}
            onChange={event => {
              setName(event.target.value);
              touch("name");
            }}
            error={!!nameError}
            helperText={nameError || t("payment.form.name_hint")}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            sx={fieldSx}
            type="email"
            id="email"
            label={`${t("payment.form.email_label")}*`}
            value={email}
            onChange={event => {
              setEmail(event.target.value.trim());
              touch("email");
            }}
            error={!!emailError}
            helperText={emailError || t("payment.form.email_hint")}
            variant="outlined"
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

// The searched journey's amount entry. It is a controlled react-hook-form field
// and is mirrored into payment context, which is what the price and pay calls
// read -- the two stores existed before this template and it does not add one.
// It always opens empty: a customer record carries no amount to pre-fill.
const SearchedAmountField = ({
  service,
  revealed,
}: {
  service: CorporateService;
  revealed: boolean;
}) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const { amount, setAmount } = usePayment();
  const entered = watch("customerAmount");
  const { field, fieldState } = useController({
    control,
    name: "customerAmount",
    defaultValue: amount,
  });
  const amountCopy = useDepositAmount(service?.amount, service?.currency, {
    hintKey: "payment.paymentForSearch.amount_hint",
    requiredKey: "errors.field_required",
  });
  // The form keeps the clean amount; the grouping exists on screen only.
  const maskedAmount = useMaskedInput({
    value: String(field.value ?? ""),
    clean: raw => cleanAmountInput(raw),
    display: displayAmount,
    onValueChange: field.onChange,
  });

  // MPAY-32393: this ran parseFloat on an unfiltered field and stored
  // String(parsed), so "100.99" was accepted and "0100.50" was renormalised
  // before anyone saw it. The field now holds exactly what the payer typed,
  // filtered to whole units at the keystroke like the manual field beside it,
  // and this effect only mirrors it into the journey state the pay call reads.
  useEffect(() => {
    if (entered === undefined) return;
    setAmount(String(entered));
  }, [entered, setAmount]);

  const amountError =
    revealed || fieldState.isDirty ? amountCopy.message(amount) : "";

  return (
    <TextInput
      id="account_number-id"
      slotProps={currencyAdornment(service?.currency)}
      fullWidth
      type="text"
      inputMode="numeric"
      variant="outlined"
      label={t("payment.paymentForSearch.amount_label")}
      placeholder={t("payment.paymentForSearch.amount_placeholder")}
      inputRef={maskedAmount.inputRef}
      value={maskedAmount.value}
      onChange={maskedAmount.onChange}
      onBlur={field.onBlur}
      name={field.name}
      error={!!amountError}
      helperText={amountError || amountCopy.hint}
    />
  );
};

// D-04's designated slot: additive, beside the template's own amount entry and
// never in place of part of it. A mandatory field this renderer cannot draw
// blocks the journey rather than being silently omitted from a payment.
const ConfiguredFieldsSlot = ({
  service,
  claims,
  onBlocked,
  onIncomplete,
  values,
  onChange,
}: {
  service: CorporateService;
  claims: FieldClaims;
  onBlocked: (blocking: string[]) => void;
  onIncomplete: (incomplete: string[]) => void;
  values: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
}) => {
  const fields = unclaimedFields(service?.submitFields ?? [], claims);

  if (fields.length === 0) return null;

  return (
    <Box mb={4}>
      <ConfiguredFields
        fields={fields}
        values={values}
        onChange={onChange}
        onUnsupported={onBlocked}
        onIncomplete={onIncomplete}
      />
    </Box>
  );
};

// A field the payer typed into and then cleared is not an answer, so it is
// dropped rather than sent as an empty value. Returns nothing at all when
// there is nothing to say, so the pay request omits the key entirely.
const answeredCdata = (values: Record<string, string>) => {
  const answered = Object.entries(values).filter(
    ([, value]) => value.trim() !== ""
  );

  return answered.length > 0 ? { cdata: Object.fromEntries(answered) } : {};
};

// The step machine both deposit templates share. It owns the transitions and
// the two calls. What it does NOT own is which steps exist or what the pay
// request carries, because those are the parts that differ per journey.
const DepositFundJourney = ({
  service,
  theme,
  corporateId,
  steps,
  collectsPayerIdentity,
  claims,
  instrumentPayload,
  renderPanel,
}: JourneyProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const token = getStorageToken();
  const form = useForm();
  const [blockedFieldIds, setBlockedFieldIds] = useState<string[]>([]);
  const [incompleteFieldIds, setIncompleteFieldIds] = useState<string[]>([]);
  const {
    currentStep,
    setCurrentStep,
    amount,
    name,
    email,
    selectedMethod,
    phoneNumber,
    customerUser,
    error,
    setError,
    // In the journey context, not the slot: the answers reach the pay call
    // and survive a return from the outcome page.
    configuredValues,
    setConfiguredValues,
    setSubmittedOutcome,
  } = usePayment();
  // Set when Continue is pressed on a step, so each field says what it lacks.
  const [revealed, setRevealed] = useState(false);
  const amountCopy = useDepositAmount(service?.amount, service?.currency, {
    hintKey: "payment.form.amount_hint",
    requiredKey: "payment.form.errors.amount_required",
  });
  const {
    setTotalAmount,
    setTotalFees,
    setInstrumentFee,
    setRegistrationFee,
    setCurrency,
  } = useGlobalContext();

  const payMutation = usePayMutation({ authorisation: token! });
  const priceMutation = usePriceMutation({ authorisation: token! });

  const paymentMethods = useQueryPaymentMethods({
    corporateId,
    enabled: !!token,
    authorisation: token || "",
  });
  const { scope, targetServiceId, homePath } = usePortal()
  const hasNoServiceList = !isServiceLink(scope, targetServiceId)

  const step = steps[currentStep - 1];

  // A wallet service pays only from a saved number; a customer with none
  // cannot pay here at all. The manual journey has no customer, so the
  // contract ignores the flag there and the payer types the number.
  const walletBlocked =
    !collectsPayerIdentity &&
    !!service?.is_supporting_customer_wallets &&
    (customerUser?.customer_wallet_collection ?? []).length === 0;

  // The deposit rail offers no country choice, so the default country applies.
  const numberCheck = useMethodNumberCheck(
    selectedMethod,
    phoneNumber,
    DEFAULT_PHONE_COUNTRY
  );

  // Browser back returns to the step recorded before this one. Going back
  // never needs new inputs, so the earlier step is set directly.
  const stepBack = useStepHistory(step, steps, target =>
    setCurrentStep(steps.indexOf(target) + 1)
  );

  // Lifting the answers out of the slot made them outlive it, so going back
  // to identify and searching a different customer would carry the previous
  // one's answers onto this payment. The slot used to remount and forget.
  useEffect(() => {
    if (step === "identify") setConfiguredValues({});
    setRevealed(false);
  }, [step, setConfiguredValues]);
  const isLoading = payMutation.isLoading || priceMutation.isLoading;

  const showFailure = (failure: unknown) =>
    setError(getFailureDisplay(failure, t, i18n.language).message);

  // The PRICE payload. D-HARM-3 and D-HARM-4 scope to the pay request only, so
  // the price call keeps naming the service the way it always has -- the two
  // are separated rather than sharing one literal, because a single object
  // cannot serve two contracts that have diverged (TODO-266).
  const basePayload = useMemo(
    () => ({
      amount: payableAmount(amount),
      corporate_subsidiary_id: service?.corporate_subsidiary_id,
      payment_method_id: selectedMethod?.id as number,
      corporate_service_id: service?.id,
    }),
    [amount, selectedMethod, service]
  );

  // The details step's output, named once. Every later step depends on it and
  // none may proceed without it, which is the step-integrity rule written as a
  // condition rather than left implicit in a step number.
  const hasDetails = () =>
    // The service's own amount settings, read once for hint and check alike.
    amountCopy.message(amount) === "" &&
    (collectsPayerIdentity
      ? name.trim() !== "" &&
      email.trim() !== "" &&
      emailRegex.test(email.trim())
      : true) &&
    selectedMethod !== null &&
    isCompleteNationalNumber(phoneNumber, DEFAULT_PHONE_COUNTRY) &&
    blockedFieldIds.length === 0 &&
    incompleteFieldIds.length === 0;

  const storePrice = (data: {
    total_fees: string;
    total_amount: string;
    instrument_fee?: string;
    registration_fee?: string;
  }) => {
    setTotalFees(data.total_fees.toString());
    setTotalAmount(data.total_amount.toString());
    // Null-safe: the components only arrive once the CPS half ships, and a
    // bare toString would blank the screen.
    setInstrumentFee(String(data.instrument_fee ?? ""));
    setRegistrationFee(String(data.registration_fee ?? ""));
  };

  const submit = () => {
    if (step === "details") {
      setRevealed(true);
      if (walletBlocked || !hasDetails()) return;
      // Covers a chosen wallet too: choosing one writes its number here.
      if (!numberCheck.check()) return;

      priceMutation.mutate(basePayload, {
        onSuccess: data => {
          // The price call resolves WITH an error envelope rather than
          // throwing, so this guard is live -- unlike the pay call's, which
          // usePayMutation made unreachable and this template therefore drops.
          if (isErrorResponse(data)) {
            showFailure(data);
            return;
          }
          storePrice(data);
          setCurrentStep(currentStep + 1);
        },
        onError: showFailure,
      });
      return;
    }

    if (step === "confirm") {
      payMutation.mutate(
        {
          ...basePayload,
          // D-HARM-13. The three fields above it are retired and still sent:
          // see PaymentPayload for why this PR adds without removing.
          core_service_id: service?.core_service_id,
          channel: "App",
          ...instrumentPayload(),
          // Omitted entirely when the service asked nothing, so a payment
          // that carries no answers sends no empty object.
          ...answeredCdata(configuredValues),
        },
        {
          // The caller owns where the payer goes next; the mutation used to
          // navigate from inside api/, the repository's one dependency-law
          // violation.
          onSuccess: data => {
            // The unit the payment was actually made in, kept beside the
            // amounts the outcome screen renders. It is the only value read
            // off the pay response other than the id -- the charge split it
            // also carries is a re-fetched pre-pay quote, not the settled
            // charge, and is deliberately not shown to a payer.
            setCurrency(data.currency);
            // Replaced, and marked as sent, so no Back reaches this Confirm
            // with the payment it already made still payable.
            const outcome = {
              to: `transaction/${data.payment_item_id}?${priceSearch(basePayload)}`,
              state: { phoneNumber, methodName: selectedMethod?.name },
            };
            setSubmittedOutcome(outcome);
            navigate(outcome.to, { replace: true, state: outcome.state });
          },
          onError: showFailure,
        }
      );
    }
  };

  if (paymentMethods?.isLoading) return <Loading />;

  if (paymentMethods?.isError) {
    navigate(-1);
    return null;
  }

  const methods = (paymentMethods?.data as PaymentMethod[]) || [];

  // Identification has no forward action of its own: the search result is what
  // advances the journey, so a Continue button here would offer a way past a
  // step whose output nothing has produced yet.
  const showsActions = step !== "identify";

  const configuredFields =
    step === "details" ? (
      <ConfiguredFieldsSlot
        service={service}
        claims={claims}
        onBlocked={setBlockedFieldIds}
        onIncomplete={setIncompleteFieldIds}
        values={configuredValues}
        onChange={(fieldId, value) =>
          setConfiguredValues(previous => ({ ...previous, [fieldId]: value }))
        }
      />
    ) : null;

  const leave = hasNoServiceList ? () => navigate(homePath) : undefined;

  return (
    <Box sx={pageColumn}>
      <FormProvider {...form}>
        <form onSubmit={() => null}>
          <JourneyFrame
            serviceName={service?.service_name}
            provider={frameProvider(scope)}
            logoSrc={service?.logo?.url}
            onLeave={leave}
            eyebrow={t("journey.deposit_eyebrow")}
            title={t(STEP_TITLES[step])}
            description={t(STEP_DESCRIPTIONS[step])}
            stepNames={steps.map(name => t(STEP_NAMES[name]))}
            stepIndex={currentStep - 1}
            onBack={
              currentStep > 1
                ? () => stepBack(() => setCurrentStep(currentStep - 1))
                : undefined
            }
            forward={
              showsActions
                ? {
                    label:
                      step === "details"
                        ? t("payment.actions.continue")
                        : t("payment.actions.confirm"),
                    onClick: submit,
                    // Continue on details stays pressable so a press can show
                    // each missing answer; confirm still needs the details.
                    disabled:
                      step === "details" ? walletBlocked : !hasDetails(),
                  }
                : undefined
            }
            busy={isLoading}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>{t("errors.title")}</AlertTitle>
                {error}
              </Alert>
            )}

            {blockedFieldIds.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("configuredFields.unsupported.blocking")}
              </Alert>
            )}

            {renderPanel(
              step,
              methods,
              configuredFields,
              numberCheck.message,
              revealed
            )}
          </JourneyFrame>
        </form>
      </FormProvider>
    </Box>
  );
};

// The method group both deposit journeys share: tiles, then the number to
// debit, or the customer's saved wallets on a wallet service.
const MethodSection = ({
  service,
  methods,
  numberError,
  revealed,
  searched,
}: {
  service: CorporateService;
  methods: PaymentMethod[];
  numberError: string;
  revealed: boolean;
  // Saved wallets belong to a found customer; only then does the flag apply.
  searched: boolean;
}) => {
  const { t } = useTranslation();
  const { selectedMethod } = usePayment();

  return (
    <FormSection title={t("payment.form.method_title")}>
      <PaymentMethods
        payments={methods}
        showTitle={false}
        error={
          revealed && !selectedMethod
            ? t("payment.form.errors.method_required")
            : ""
        }
      />
      <PhoneNumberInputs
        service={service}
        numberError={numberError}
        revealed={revealed}
        usesSavedWallets={
          searched && !!service?.is_supporting_customer_wallets
        }
        isVertical
      />
    </FormSection>
  );
};

/**
 * DEPOSITFUNDS_MANUAL. The payer types the amount and their own name and email,
 * chooses an instrument, and confirms a priced summary.
 */
export const DepositFundManualTemplate = (props: FlowTemplateProps) => {
  const { name, email, phoneNumber } = usePayment();

  return (
    <DepositFundJourney
      {...props}
      steps={DEPOSIT_FUND_MANUAL_STEPS}
      collectsPayerIdentity
      claims={DEPOSIT_FUND_MANUAL_CLAIMS}
      instrumentPayload={() => ({
        instrument_account_owner: name,
        instrument_account_number: phoneNumber,
        notification_email: email,
      })}
      renderPanel={(step, methods, configuredFields, numberError, revealed) => (
        <Paper
          elevation={0}
          sx={{ width: "100%", boxSizing: "border-box", bgcolor: "transparent" }}
        >
          {step === "details" ? (
            <>
              <PayerDetailsFields service={props.service} revealed={revealed} />
              {configuredFields}
              <MethodSection
                service={props.service}
                methods={methods}
                numberError={numberError}
                revealed={revealed}
                searched={false}
              />
            </>
          ) : (
            <DepositReview currency={props.service?.currency} searched={false} />
          )}
        </Paper>
      )}
    />
  );
};

/**
 * DEPOSITFUNDS_SEARCH. The customer is found by their code first; the customer
 * card then stands beside amount entry so the payer can see who they pay for.
 */
export const DepositFundSearchTemplate = (props: FlowTemplateProps) => {
  const { t } = useTranslation();
  const { selectedWallet, customerUser, phoneNumber } = usePayment();

  return (
    <DepositFundJourney
      {...props}
      steps={DEPOSIT_FUND_SEARCH_STEPS}
      collectsPayerIdentity={false}
      claims={DEPOSIT_FUND_SEARCH_CLAIMS}
      instrumentPayload={() => ({
        customer_id: customerUser?.id,
        customer_wallet_id: selectedWallet?.id,
        instrument_account_number: phoneNumber || undefined,
      })}
      renderPanel={(step, methods, configuredFields, numberError, revealed) => (
        <Paper
          elevation={0}
          sx={{ width: "100%", boxSizing: "border-box", bgcolor: "transparent" }}
        >
          {step === "identify" && (
            <SearchCustomerForm corporateId={props.corporateId} />
          )}

          {step !== "identify" && (
            // Card beside the form from sm up, stacked above it on a phone.
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 2fr) minmax(0, 3fr)" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <CustomerCard currency={props.service?.currency} />
              <Box sx={{ minWidth: 0 }}>
                {step === "confirm" && (
                  <DepositReview currency={props.service?.currency} searched />
                )}
                {step === "details" && (
                  <>
                    <FormSection title={t("payment.form.transaction_title")}>
                      <SearchedAmountField
                        service={props.service}
                        revealed={revealed}
                      />
                    </FormSection>
                    {configuredFields}
                    <MethodSection
                      service={props.service}
                      methods={methods}
                      numberError={numberError}
                      revealed={revealed}
                      searched
                    />
                  </>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    />
  );
};
